import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ConfigLayout.scss';
import { configMenuItems } from './constants';
import { usePermissions } from '../../context/PermissionsContext';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { configLayoutI18n } from './i18n';

const ConfigLayout = ({ DesignSystemComponents, title }) => {
  const { MenuItem, Card } = DesignSystemComponents;
  const location = useLocation();
  const { hasConfigSectionPermission } = usePermissions();
  const { language } = useLanguage();
  const configLayoutLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configLayoutI18n });

  useEffect(() => {
    configLayoutI18n.changeLanguage(configLayoutLanguage);
  }, [configLayoutLanguage]);

  const resolvedTitle = title || t('config_layout_title');

  const { topEntries, bottomEntries } = useMemo(() => {
    const top = configMenuItems.filter((el) => !el.bottom);
    const bottom = configMenuItems.filter((el) => el.bottom);
    return { topEntries: top, bottomEntries: bottom };
  }, []);

  const renderItem = (item, index, parentId = '') => {
    if (item.requiredCode && !hasConfigSectionPermission(item.requiredCode)) {
      return null;
    }

    const id = parentId ? `${parentId}-${index}` : `${index}`;

    const isActive = location.pathname === item.linkTo;
    const hasActiveChild =
      item.children?.some(
        (child) =>
          location.pathname === child.linkTo ||
          child.children?.some((grand) => location.pathname === grand.linkTo)
      ) || false;

    const text = t(item.linkKey || '');

    if (item.children?.length) {
      return (
        <MenuItem
          key={`cfg-menu-${id}`}
          icon={item.icon}
          linkText={text}
          itemId={id}
          className={`mb-1 ${isActive || hasActiveChild ? 'selected' : ''}`}
        >
          <div>
            {item.children.map((child, idx) => renderItem(child, idx, id))}
          </div>
        </MenuItem>
      );
    }

    return (
      <NavLink
        key={`cfg-lnk-${id}`}
        to={item.linkTo || '#'}
        end={false}
        className='w-100 d-block'
      >
        {({ isActive: navIsActive }) => {
          const computed =
            parentId !== ''
              ? navIsActive
                ? 'selected-submenu'
                : ''
              : navIsActive
                ? 'selected'
                : '';
          return (
            <MenuItem
              className={`mb-1 ${computed}`}
              icon={item.icon}
              linkText={text}
              disabled={item.disabled}
              btnIdName={`CFG_BTN_menu_${id}`}
              svgIconIdName={`CFG_ICO_menu_${id}`}
              textIdName={`CFG_LBL_menu_${id}`}
            />
          );
        }}
      </NavLink>
    );
  };

  return (
    <section className='config-layout d-flex container-fluid w-100'>
      <aside
        className='config-sidebar'
        aria-label={t('config_layout_sidebar_aria')}
      >
        <Card specialPadding='p-3'>
          <nav className='config-nav-wrapper'>
            <h3 id='config-title' className='fw-bold mb-3'>
              {resolvedTitle}
            </h3>
            <div className='config-nav-scroll'>
              {topEntries.map((el, idx) => renderItem(el, idx))}
            </div>

            {bottomEntries.length > 0 && (
              <>
                <hr
                  className='config-separator'
                  aria-orientation='horizontal'
                />
                <div className='config-nav-scroll'>
                  {bottomEntries.map((el, idx) => renderItem(el, idx))}
                </div>
              </>
            )}
          </nav>
        </Card>
      </aside>
      <section
        className='config-content container-fluid'
        role='region'
        aria-labelledby='config-title'
      >
        <header className='config-header'>
          {/* place for page-level actions (e.g., Editar / Guardar) */}
        </header>

        <div className='config-page'>
          <Outlet context={{ DesignSystemComponents }} />
        </div>
      </section>
    </section>
  );
};

export default ConfigLayout;
